import { mapValues, mapKeys } from "lodash";
import React, { Fragment } from "react";
import classNames from "classnames";
import memoize from "memoize-weak";
import bind from "memoize-bind";

import BigCalendar, { withDragAndDrop, withTimezoneAwareness } from "lib/utils/react-big-calendar/index";
import { Button, Menu, Dimmer, Link, Loader } from "lib/components/vendor";
import StockAvailability from "./StockAvailability";
import styles from "./SlotsCalendar.less";
import st from "lib/utils/index";

const WrappedBigCalendar = withTimezoneAwareness(BigCalendar);
const WrappedDragAndDropBigCalendar = withDragAndDrop(WrappedBigCalendar, {
  withDnDContext: false,
});

const timeRangeFormat = ({ start, end }, culture, local) =>
  local.format(start, moment.TIME_FORMAT, culture) + " — " + local.format(end, moment.TIME_FORMAT, culture);
const timeRangeStartOnlyFormat = ({ start, end }, culture, local) => local.format(start, moment.TIME_FORMAT, culture);
const dateRangeFormat = ({ start, end }, culture, local) =>
  local.format(
    start,
    (moment(start).isSame(end, "month") ? "D" : "D MMMM") + (moment(start).isSame(end, "year") ? "" : " YYYY"),
    culture
  ) +
  " - " +
  local.format(end, "D MMMM YYYY", culture);
const weekRangeFormat = dateRangeFormat;

const formats = {
  dateFormat: "DD",
  dayFormat: "ddd DD/MM",
  weekdayFormat: "ddd",
  selectRangeFormat: timeRangeFormat,
  eventTimeRangeFormat: timeRangeStartOnlyFormat,
  timeGutterFormat: moment.TIME_FORMAT,
  monthHeaderFormat: "MMMM YYYY",
  dayHeaderFormat: "dddd, D MMMM YYYY",
  dayRangeHeaderFormat: weekRangeFormat,
  agendaHeaderFormat: dateRangeFormat,
  agendaDateFormat: "ddd, D MMM",
  agendaTimeFormat: moment.TIME_FORMAT,
  agendaTimeRangeFormat: timeRangeFormat,
};

const getMessages = memoize((locale) => ({
  allDay: I18n.t("papp.components.SlotsCalendar.allDay"),
  previous: I18n.t("papp.components.SlotsCalendar.previous"),
  next: I18n.t("papp.components.SlotsCalendar.next"),
  today: I18n.t("papp.components.SlotsCalendar.today"),
  month: I18n.t("papp.components.SlotsCalendar.month"),
  week: I18n.t("papp.components.SlotsCalendar.week"),
  day: I18n.t("papp.components.SlotsCalendar.day"),
  agenda: I18n.t("papp.components.SlotsCalendar.agenda"),
  showMore: (i) => I18n.t("papp.components.SlotsCalendar.showMore", { count: i }),
  date: I18n.t("papp.components.SlotsCalendar.date"),
  time: I18n.t("papp.components.SlotsCalendar.time"),
  event: I18n.t("papp.components.SlotsCalendar.event"),
}));

const titleAccessor = (event) => event.product_full_name;

const EventComponent = ({ event, mode, showProductName, selectSlot, showBookButton }) => (
  <div className={styles["event"]}>
    <div className={styles["event-content"]}>
      {showProductName && !!event.product_full_name && (
        <div className={styles["event-name"]}>
          {event.availability_type === "available_with_instant_booking" && (
            <img
              src="/images/flash.svg"
              title={I18n.humanAttributeName("product_schedule_slot", "available_with_instant_booking")}
              style={{ width: "17px", paddingTop: "1px", float: "left" }}
            />
          )}{" "}
          <strong>{event.supplier_name}</strong> <br />
          {event.product_full_name}
        </div>
      )}

      <div className={styles["event-pricing"]}>
        {st.formatMoney(event.default_product_pricelist_item_price)}{" "}
        {st.authorize.company.agent &&
          st.authorize.company.name !== event.supplier_name &&
          "(" + st.formatMoney(event.potential_agent_commission_fee) + ")"}
      </div>
      {showBookButton && (
        <div className="event-buttons">
          <Link
            as="button"
            size="tiny"
            to={`/c/${st.authorize.company.id}/calendar?showSlot=${event.id}&action=book`}
            content={"Book now"}
          >
            <Button as="button" size="tiny" content={"Book now"} />
          </Link>

          <Button as="a" size="tiny" onClick={selectSlot} target="_blank" content={"Details"} />
        </div>
      )}
      <div className={styles["event-availability"]}>
        {mode === "availability" && <StockAvailability slot={event} />}
        {mode === "price" &&
          (event.default_product_pricelist_item_price ? (
            st.formatMoney(event.default_product_pricelist_item_price)
          ) : (
            <span>&nbsp;</span>
          ))}
      </div>
    </div>
  </div>
);

const MonthEventComponent = ({ event, mode, showProductName }) => (
  <div className={styles["month-event"]}>
    {event.includes_time && (
      <div className={styles["month-event-time"]}>{moment.parseZone(event.starts_at).format(moment.TIME_FORMAT)}</div>
    )}
    <div className={styles["month-event-content"]}>
      {showProductName && !!event.product_full_name && (
        <div className={styles["month-event-name"]}>{event.product_full_name}</div>
      )}
      <div className={styles["month-event-availability"]}>
        {mode === "availability" && <StockAvailability slot={event} />}
        {mode === "price" &&
          (event.default_product_pricelist_item_price ? (
            st.formatMoney(event.default_product_pricelist_item_price)
          ) : (
            <span>&nbsp;</span>
          ))}
      </div>
    </div>
  </div>
);

const AgendaEventComponent = ({ className, onClick, ...props }) => (
  <div className={styles["agenda-event"] + " rbc-event " + (className || "")}>
    <EventComponent selectSlot={onClick} showBookButton {...props} />
  </div>
);

const ToolbarComponent = ({
  label,
  views,
  view,
  onViewChange,
  onNavigate,
  messages,
  filters,
  mode,
  loading,
  onModeChange,
}) => (
  <div className={styles["rbc-toolbar"]}>
    <span className={styles["rbc-toolbar-left"]}>
      <Button.Group basic>
        <a className="ui button" onClick={() => onNavigate(BigCalendar.Navigate.TODAY)}>
          {messages.today}
        </a>
      </Button.Group>
      &nbsp;&nbsp;
      <Button.Group basic>
        <a className="ui button icon" onClick={() => onNavigate(BigCalendar.Navigate.PREVIOUS)}>
          <i className="material-icons icon">chevron_left</i>
        </a>
        <a className="ui button icon" onClick={() => onNavigate(BigCalendar.Navigate.NEXT)}>
          <i className="material-icons icon">chevron_right</i>
        </a>
      </Button.Group>
      &nbsp;&nbsp;
      {filters && <span className={styles["filters"]}>{filters}</span>}
    </span>

    <span className={styles["rbc-toolbar-label"]}>{label}</span>

    <span className={styles["rbc-toolbar-right"]}>
      <span className={styles["rbc-toolbar-loader"]}>
        <Loader inline active={loading} />
      </span>

      <Menu color="blue" secondary pointing compact className="stMenuTabs u-marginRight">
        <Menu.Item
          active={mode === "availability"}
          onClick={() => onModeChange("availability")}
          content={I18n.t("papp.components.SlotsCalendar.availability_mode")}
        />
        <Menu.Item
          active={mode === "price"}
          onClick={() => onModeChange("price")}
          content={I18n.t("papp.components.SlotsCalendar.price_mode")}
        />
      </Menu>
      <Menu color="teal" secondary pointing compact className="stMenuTabs">
        {views.map((v) => (
          <Menu.Item key={v} active={view === v} onClick={() => onViewChange(v)} content={messages[v]} />
        ))}
      </Menu>
    </span>
  </div>
);

const getCalendarComponents = memoize(
  (mode, filters, loading, showProductName, onModeChange, eventPropGetter, onSelectEvent) => {
    return {
      event: (props) => <EventComponent mode={mode} showProductName={showProductName} {...props} />,
      month: {
        event: (props) => <MonthEventComponent mode={mode} showProductName={showProductName} {...props} />,
      },
      agenda: {
        event: (props) => (
          <AgendaEventComponent
            mode={mode}
            showProductName={showProductName}
            onClick={bind(onSelectEvent, null, props.event)}
            {...eventPropGetter(props.event)}
            {...props}
          />
        ),
      },
      toolbar: (props) => (
        <ToolbarComponent mode={mode} loading={loading} onModeChange={onModeChange} filters={filters} {...props} />
      ),
    };
  }
);

const getEventPropGetter = memoize((modifiers) => (event) => {
  return {
    className: classNames(
      mapValues(
        mapKeys(modifiers || {}, (_, key) => `rbc-event--${key}`),
        (fn) => fn(event)
      )
    ),
  };
});

// hide month Week Day for reseller

const getViews = memoize((withAgenda) => ["month", "week", "day"].concat(withAgenda ? ["agenda"] : []));

const mapSlotsToEvents = memoize((slots, minDate, maxDate) => {
  return slots
    .filter((slot) => moment(slot.starts_at).isBetween(minDate, maxDate, "day", "[]"))
    .map((slot) => ({
      id: slot.id,
      uuid: slot.uuid,
      company_id: slot.company_id,
      starts_at: slot.starts_at,
      ends_at: slot.ends_at,
      includes_time: slot.includes_time,
      supplier_name: slot.supplier_name,
      product_full_name: slot.product_full_name,
      total_stock_used: slot.total_stock_used,
      total_stock_limit: slot.total_stock_limit,
      total_stock_left: slot.total_stock_left,
      default_product_pricelist_item_price: slot.default_product_pricelist_item_price,
      active: slot.active,
      confirmed: slot.confirmed,
      awaiting_for_company: slot.awaiting_for_company,
      awaiting_for_customer: slot.awaiting_for_customer,
      booked: slot.booked,
      start: slot.starts_at,
      end: slot.ends_at,
      allDay: !slot.includes_time,
      stock_type: slot.stock_type,
      availability_type: slot.availability_type,
      potential_agent_commission_fee: slot.potential_agent_commission_fee,
    }));
});

const getDefaultDateForSlots = memoize((slots) => {
  const firstFutureActiveSlot = slots.find((slot) => slot.active && slot.in_future);
  return firstFutureActiveSlot ? new Date(firstFutureActiveSlot.starts_at) : new Date();
});

const selectedEvents = {};

export default class SlotsCalendar extends React.PureComponent {
  constructor(...args) {
    super(...args);
    const defaultDate = this.props.defaultDate || getDefaultDateForSlots(this.props.slots);
    const defaultView = st.authorize.company.reseller ? "agenda" : this.props.defaultView || "month";
    const defaultMode = this.props.defaultMode || "availability";
    this.state = {
      date: new Date(defaultDate.setHours(0, 0, 0, 0)),
      view: defaultView,
      mode: defaultMode,
    };
  }

  handleSelectEvent(event) {
    const { onSlotSelect, slots } = this.props;

    onSlotSelect && onSlotSelect(slots.find((sl) => sl.uuid === event.uuid));
  }

  handleSelectSlot({ start, end, slots: dates }) {
    const { onDateRangeSelect } = this.props;
    onDateRangeSelect &&
      onDateRangeSelect({
        starts_at: start,
        ends_at: end,
        dates,
      });
  }

  handleEventDrop({ event, start, end }) {
    const { slots, onSlotMove } = this.props;
    onSlotMove &&
      onSlotMove({
        starts_at: start,
        ends_at: end,
        slot: slots.find((sl) => sl.uuid === event.uuid),
      });
  }

  handleModeChange(mode) {
    this.setState({ mode });
  }

  handleNavigate(date, view, action) {
    this.setState({ date }, () => {
      this.triggerNavigate();
    });
  }

  handleViewChange(view) {
    this.setState({ view }, () => {
      // this.triggerNavigate()
    });
  }

  triggerNavigate() {
    const { date, view } = this.state;
    this.props.onNavigate &&
      this.props.onNavigate({
        currentDate: moment(date).format(moment.RUBY_DATE_FORMAT),
        minVisibleDate: this.minVisibleDate,
        maxVisibleDate: this.maxVisibleDate,
      });
  }

  get minVisibleDate() {
    return moment(this.state.date).startOf("month").add(-7, "days").format(moment.RUBY_DATE_FORMAT);
  }

  get maxVisibleDate() {
    return moment(this.state.date).endOf("month").add(7, "days").format(moment.RUBY_DATE_FORMAT);
  }

  render() {
    const { date, view, mode } = this.state;
    const {
      filters,
      loading = false,
      showLoadingDimmer = false,
      modifiers,
      onDateRangeSelect,
      onSlotMove,
      onSlotSelect,
      showProductName = true,
      slots,
      timeZoneName,
      withAgenda = false,
    } = this.props;
    const filterForCompany = (arr) => {
      if (st.authorize.company.reseller) {
        return ["agenda"];
      }
      return arr;
    };

    const onNavigate = bind(this.handleNavigate, this);
    const onSelectEvent = onSlotSelect && bind(this.handleSelectEvent, this);
    const onSelectSlot = onDateRangeSelect && bind(this.handleSelectSlot, this);
    const onEventDrop = onSlotMove && bind(this.handleEventDrop, this);
    const onModeChange = bind(this.handleModeChange, this);
    const onView = bind(this.handleViewChange, this);
    const views = filterForCompany(getViews(withAgenda));

    const eventPropGetter = getEventPropGetter(modifiers);
    const components = getCalendarComponents(
      mode,
      filters,
      loading,
      showProductName,
      onModeChange,
      eventPropGetter,
      onSelectEvent
    );
    const bigCalendarProps = {
      components,
      date,
      eventPropGetter,
      events: mapSlotsToEvents(slots, this.minVisibleDate, this.maxVisibleDate),
      formats,
      messages: getMessages(I18n.locale),
      onEventDrop,
      onNavigate,
      onSelectEvent,
      onSelectSlot,
      onView,
      popup: true,
      selectable: true,
      selected: selectedEvents,
      timeZoneName,
      titleAccessor,
      view,
      views,
      length: 7,
    };

    return (
      <div style={{ position: "relative", height: "100%" }}>
        <Dimmer inverted active={showLoadingDimmer}>
          <Loader />
        </Dimmer>
        {!onSlotMove && <WrappedBigCalendar {...bigCalendarProps} />}
        {!!onSlotMove && <WrappedDragAndDropBigCalendar {...bigCalendarProps} />}
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/lib/components/SlotsCalendar.jsx